import { useState, useEffect, useContext } from "react";
import { Button, Flex, Modal, Typography, App, Pagination } from "antd";

import PlusIcon from "@/icons/PlusIcon";

import SprintStatusesTable from "@/components/tables/SprintStatusesTable";
import SprintStatusModal from "@/components/modals/SprintStatusModal";
import Breadcrumbs from "@/components/ui/Breadcrumbs";

import { UserContext } from "@/context/UserContext";
import MainContext from "@/layouts/MainLayout/context";

import { useTableAutoHeight } from "@/hooks/useTableAutoHeight";

import { getList, deleteItem } from "@/api/sprintStatuses";

const LOCAL_STORAGE_KEY = "sprintStatusesTableParams";

const { Title } = Typography;

// Страница статусов спринтов
// Содержит список статусов с возможностью добавления, редактирования и удаления
const SprintStatuses = () => {
  const { message } = App.useApp();
  const { user } = useContext(UserContext);

  const { config } = useContext(MainContext);
  const sprintStatusesName =
    config?.entities?.sprint?.ui?.statusesTitle || "Статусы спринтов";

  // Данные
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Инициализация пагинации из localStorage
  const initialTableParams = JSON.parse(
    localStorage.getItem(`${user.user_uuid}_${LOCAL_STORAGE_KEY}`)
  ) || {
    pagination: {
      current: 1,
      pageSize: 10,
      position: ["bottomLeft"],
      showSizeChanger: true,
    },
  };

  const [tableParams, setTableParams] = useState(initialTableParams);

  // Автоматическая высота таблицы
  const { tableContainerRef, tableHeight, headerHeight } = useTableAutoHeight([
    data,
    tableParams.pagination.pageSize,
  ]);

  // Окно создания/редактирования
  const [isModalShow, setIsModalShow] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // Запрос на сервер для получения данных
  const fetchData = async (pagination, sorter) => {
    setIsLoading(true);

    const params = {
      page: pagination?.current || tableParams.pagination.current,
      perpage: pagination?.pageSize || tableParams.pagination.pageSize,
    };

    if (sorter) {
      const sortArray = Array.isArray(sorter) ? sorter : [sorter];

      const sortString = sortArray
        .map(({ field, order }) => {
          return order === "descend" ? `-${field}` : field;
        })
        .join(",");

      params.sort = sortString;
    }

    try {
      const { items, total, page, perPage } = await getList(params);
      setData(items);
      const updatedTableParams = {
        pagination: {
          ...tableParams.pagination,
          total,
          current: page,
          pageSize: perPage,
        },
      };
      setTableParams(updatedTableParams);

      // Сохраняем параметры пагинации в localStorage
      localStorage.setItem(
        `${user.user_uuid}_${LOCAL_STORAGE_KEY}`,
        JSON.stringify(updatedTableParams)
      );
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
    } finally {
      setIsLoading(false);
    }
  };

  //  initial fetch
  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  const handleTableChange = async (pagination, filters, sorter) => {
    fetchData(pagination, sorter);
  };

  const handlePaginationChange = (current, pageSize) => {
    // При изменении pageSize сбрасываем страницу на 1
    const shouldResetPage = pageSize !== tableParams.pagination.pageSize;

    const newPagination = {
      ...tableParams.pagination,
      current: shouldResetPage ? 1 : current,
      pageSize,
    };

    fetchData(newPagination, tableParams.sorter);
  };

  // Обработчик добавления нового статуса
  const handleAddNew = () => {
    setCurrentItem({ idx: data.length });
    setIsModalShow(true);
  };

  // Обработчик редактирования статуса
  const handleEditItem = (item) => {
    setCurrentItem(item);
    setIsModalShow(true);
  };

  // Обработчик сохранения статуса
  const handleSaveItem = () => {
    setIsModalShow(false);
    if (currentItem) {
      fetchData();
    } else {
      fetchData({ current: 1 });
    }
  };

  // Обработчик удаления статуса
  const handleDeleteItem = (item) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить статус ${item.name}?`,
      content: "Это можно сделать только если он нигде не установлен.",
      onOk() {
        handleConfirmDelete(item);
      },
      okText: "Удалить",
      cancelText: "Отмена",
    });
  };

  // Действительно удаляем статус после подтверждения
  const handleConfirmDelete = async (item) => {
    setIsLoading(true);
    try {
      await deleteItem({ uuid: item.uuid });
      await fetchData();
    } catch (error) {
      message.open({
        type: "error",
        content: error.message,
        duration: 10,
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Flex vertical gap={25} className="h-100">
        <Flex vertical gap={10}>
          <Breadcrumbs
            items={[
              {
                title: "Административная панель",
              },
              {
                title: "Системные справочники",
              },
              {
                title: sprintStatusesName,
              },
            ]}
          />
          <Flex gap={20} align="center" justify="space-between">
            <Title level={2} className="no-margin">
              {sprintStatusesName}
            </Title>

            <Button
              type="primary"
              icon={<PlusIcon />}
              onClick={handleAddNew}
            ></Button>
          </Flex>
        </Flex>

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <SprintStatusesTable
            data={data}
            loading={isLoading}
            pagination={false}
            onChange={handleTableChange}
            onEditItem={handleEditItem}
            onDeleteItem={handleDeleteItem}
            scroll={{ x: "100%", y: tableHeight - headerHeight }}
          />
        </div>

        <div style={{ minHeight: 32 }}>
          <Pagination
            {...tableParams.pagination}
            onChange={handlePaginationChange}
          />
        </div>
      </Flex>

      {isModalShow && (
        <SprintStatusModal
          data={currentItem}
          visible={isModalShow}
          onSave={handleSaveItem}
          onCancel={() => setIsModalShow(false)}
        />
      )}
    </>
  );
};

export default SprintStatuses;
